import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'
import EditIcon from '@mui/icons-material/Edit'
import DownloadIcon from '@mui/icons-material/Download'

import axios from '../../dataClient/dataClient'
import { handleDownloadResponse } from '../../util/downloadFile'
import { attachmentStatuses } from '../../util/attachmentConsts'
import { FlexColumn, FlexRow } from '../styles/Containers'
import { ContentRow } from '../styles/TableStyles'
import { BoldMedBodyText, MedBodyText, ErrorText } from '../styles/Text'
import { SecondaryIconButton } from '../common/Button'
import AssignedUserChip from '../common/AssignedUserChip'
import AttachmentStatus from '../foiAttachments/AttachmentStatus'

const UserContainer = styled(FlexRow)`
  flex-basis: 12%;
  min-width: 75px;
  align-items: center;
`

const LetterNameContainer = styled.div`
  flex-basis: ${props => (props.$large ? '50%' : '38%')};
`

const DateTimeContainer = styled(FlexRow)`
  flex-basis: ${props => (props.$large ? '33%' : '25%')};
  gap: 5px;
  flex-wrap: wrap;
`

const StatusContainer = styled(FlexRow)`
  flex-basis: 20%;
  justify-content: flex-end;
  text-align: right;
`

const ActionContainer = styled(FlexColumn)`
  width: 29px;
  flex-shrink: 0;
  align-items: flex-end;
  gap: 0;
`

const MiniErrorText = styled(ErrorText)`
  font-size: 10px;
  text-align: right;
`

function LetterRow({ letter, onEditLetter = () => {}, handleSelectLetter = () => {} }) {
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [downloadError, setDownloadError] = useState(false)

  function handleSelect() {
    handleSelectLetter(letter.id)
  }

  function handleRowKeyDown(event) {
    if (event.target !== event.currentTarget) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect()
    }
  }

  function handleEditClick(event) {
    event.stopPropagation()
    if (letter.state === 'DRAFT') onEditLetter(letter.id)
  }

  function handleDownloadClick(event) {
    event.stopPropagation()
    if (letter.state !== 'SENT') return

    setDownloadError(false)
    setDownloadLoading(true)
    const fileName = `${letter.name}.pdf`

    axios
      .get(`/letter/${letter.id}/download`, { responseType: 'blob' })
      .then(response => handleDownloadResponse(response, fileName, 'download'))
      .catch(e => {
        setDownloadError(true)
        console.error('an error occurred while downloading the letter', letter.name, e)
      })
      .finally(() => {
        setDownloadLoading(false)
      })
  }

  const isDraft = letter.state === 'DRAFT'
  const assignedUser = isDraft ? letter.assignedTo : letter.sentBy

  return (
    <ContentRow role="button" tabIndex={0} onClick={handleSelect} onKeyDown={handleRowKeyDown}>
      <UserContainer>
        {assignedUser && (
          <AssignedUserChip
            firstName={assignedUser.firstName}
            lastName={assignedUser.lastName}
            color={assignedUser.chipColor}
            isShort
            labelText={isDraft ? 'Assigned to' : 'Sent by'}
          />
        )}
      </UserContainer>
      <LetterNameContainer $large={!isDraft}>
        <BoldMedBodyText>{letter.name}</BoldMedBodyText>
      </LetterNameContainer>
      <DateTimeContainer $large={!isDraft}>
        <BoldMedBodyText>{letter.date ? format(letter.date, 'MMM-dd-yyyy') : '—'}</BoldMedBodyText>
        {letter.date && <MedBodyText>{format(letter.date, 'hh:mm a')}</MedBodyText>}
      </DateTimeContainer>
      {isDraft && <StatusContainer>{letter.status && <AttachmentStatus status={letter.status} />}</StatusContainer>}
      <ActionContainer>
        {letter.state === 'DRAFT' && (
          <SecondaryIconButton onClick={handleEditClick} aria-label={`Edit letter ${letter.name}`}>
            <EditIcon />
          </SecondaryIconButton>
        )}
        {letter.state === 'SENT' && (
          <SecondaryIconButton loading={downloadLoading} onClick={handleDownloadClick} aria-label={`Download letter ${letter.name} as PDF`}>
            <DownloadIcon />
          </SecondaryIconButton>
        )}
        {downloadError && <MiniErrorText role="alert">Error, try again.</MiniErrorText>}
      </ActionContainer>
    </ContentRow>
  )
}

LetterRow.propTypes = {
  letter: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired,
    name: PropTypes.string.isRequired,
    date: PropTypes.instanceOf(Date),
    status: PropTypes.oneOf(attachmentStatuses),
    assignedTo: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      chipColor: PropTypes.string.isRequired
    }),
    sentBy: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      chipColor: PropTypes.string.isRequired
    })
  }).isRequired,
  onEditLetter: PropTypes.func,
  handleSelectLetter: PropTypes.func
}

export default LetterRow
